/**
 * AdminSweep — /app/admin/users
 *
 * Bloq-team admin destructive sweep. Three tabs:
 *   • Users — list every account, select non-founders, cascade-delete them
 *     along with the workspaces they own.
 *   • Workspaces — list workspaces YOU own (founder); nuke the
 *     leftover test workspaces (BULK_OTHER_WS, SEARCH_ISOLATION_WS, etc.).
 *   • Operational — flush email_queue, old email_logs, oauth_states,
 *     orphaned meeting rows.
 *
 * Confirmation flow: every destructive action opens a modal that
 * shows the row counts that will be affected and requires the admin
 * to type `DELETE` into a textbox before the submit button enables.
 * Server enforces the same token (defence-in-depth).
 *
 * Route is wrapped in <AdminRoute> at App.js so non-admins get bounced.
 */
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Users as UsersIcon,
  Layers,
  Trash2,
  Loader2,
  Search,
  X as XIcon,
  CheckSquare,
  Square,
  UserPlus,
  Mail,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// Heuristic the admin uses to pre-select "obvious test users" in one
// click. Pattern matches were derived from the iter-25..28 testing-agent
// fixtures and the older bulk-test seeds.
const TEST_USER_PATTERNS = [
  /^test_[a-f0-9]+@bloq\.video$/i,
  /^out_[a-f0-9]+@example\.com$/i,
  /^outsider_[a-f0-9]+@example\.com$/i,
  /^blocked_[a-f0-9]+@example\.com$/i,
  /^member_[a-f0-9]+@example\.com$/i,
  /^bulk_outsider_[a-f0-9]+@example\.com$/i,
  /^notes_outsider_[a-f0-9]+@example\.com$/i,
  /^m1055cross_[a-f0-9]+@example\.com$/i,
  /^longpw_\d+@example\.com$/i,
  /^normal_\d+@example\.com$/i,
  /^nonadmin_\d+@example\.com$/i,
  /^guest_\d+@example\.com$/i,
  /@example\.com$/i,           // catch-all for synthetic accounts
];

// Workspaces named like these were created by the testing agent under
// the founder's credentials. They're the things to nuke on the
// Workspaces tab.
const TEST_WS_NAME_PATTERNS = [
  /^BULK_OTHER_WS$/,
  /^SEARCH_ISOLATION_WS$/,
  /^M1055/,
  /^TEST_WS_/,
];

const isTestUser = (u) => TEST_USER_PATTERNS.some((re) => re.test(u.email || ""));
const isTestWs = (w) => TEST_WS_NAME_PATTERNS.some((re) => re.test(w.name || ""));

export default function AdminSweep() {
  const { user } = useAuth();
  return (
    <div className="flex flex-col gap-7" data-testid="admin-sweep-page">
      <header>
        <p className="bloq-eyebrow">Bloq admin · destructive operations</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Admin Control Center</h1>
        <p className="text-sm text-[#4a4a52] mt-2 max-w-2xl">
          Bulk-delete leftover test users, workspaces, and operational debris.
          Every action requires typing <code className="bg-[#f5efe6] px-1.5 py-0.5 text-xs">DELETE</code> to
          confirm. Cascades through every related collection — the work is
          irreversible.
        </p>
      </header>

      <div className="bloq-card p-4 border-l-4 border-l-[#c43838] bg-[rgba(196,56,56,0.04)]">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#c43838] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#c43838]">
              Heads up — this affects the database you&apos;re currently signed into.
            </p>
            <p className="text-xs text-[#82828c] mt-1">
              Signed in as <strong>{user?.email}</strong>. Cleaning preview only?
              Make sure you&apos;re on the preview environment — production has a separate database.
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="signups">
        <TabsList>
          <TabsTrigger value="signups" data-testid="sweep-tab-signups">
            <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Signups
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="sweep-tab-users">
            <UsersIcon className="w-3.5 h-3.5 mr-1.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="workspaces" data-testid="sweep-tab-workspaces">
            <Layers className="w-3.5 h-3.5 mr-1.5" /> Workspaces
          </TabsTrigger>
          <TabsTrigger value="operational" data-testid="sweep-tab-operational">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Operational
          </TabsTrigger>
        </TabsList>

        <TabsContent value="signups" className="mt-5"><SignupsPanel /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersPanel /></TabsContent>
        <TabsContent value="workspaces" className="mt-5"><WorkspacesPanel /></TabsContent>
        <TabsContent value="operational" className="mt-5"><OperationalPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================== USERS TAB =============================== */

function UsersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/sweep/users`, { params: search ? { q: search } : {} });
      setRows(r.data.users || []);
      // Drop any selected ids that no longer exist in the new result set.
      setSelected((prev) => {
        const next = new Set();
        const seen = new Set((r.data.users || []).map((u) => u.user_id));
        for (const id of prev) if (seen.has(id)) next.add(id);
        return next;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const eligible = useMemo(() => filtered.filter((u) => !u.is_self && !u.is_admin), [filtered]);

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const selectAllVisible = () => setSelected(new Set(eligible.map((u) => u.user_id)));
  const selectTestPatterns = () =>
    setSelected(new Set(eligible.filter(isTestUser).map((u) => u.user_id)));
  const clearSelection = () => setSelected(new Set());

  const runSweep = async (confirmToken) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/sweep/users`, {
        user_ids: Array.from(selected),
        confirm_token: confirmToken,
      });
      const summary = r.data.summary || {};
      const lines = Object.entries(summary).map(([k, v]) => `${k}: ${v}`);
      toast.success(
        `Deleted ${r.data.user_ids_processed} user(s) + ${r.data.workspaces_cascade_deleted} workspace(s).` +
        (lines.length ? `\n${lines.slice(0, 4).join(", ")}…` : ""),
        { duration: 6000 },
      );
      setConfirmOpen(false);
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by email or name…"
            className="bloq-input pl-9 pr-9 w-full"
            data-testid="sweep-users-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
            >
              <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
            </button>
          )}
        </div>
        <button
          onClick={selectTestPatterns}
          className="bloq-btn-secondary text-xs px-3 py-2"
          data-testid="sweep-select-test-patterns"
        >
          Select test patterns
        </button>
        <button
          onClick={selectAllVisible}
          className="bloq-btn-secondary text-xs px-3 py-2"
          data-testid="sweep-select-all-visible"
        >
          Select all visible ({eligible.length})
        </button>
        {selected.size > 0 && (
          <button
            onClick={clearSelection}
            className="bloq-btn-secondary text-xs px-3 py-2"
            data-testid="sweep-clear-selection"
          >
            Clear
          </button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[#82828c]" data-testid="sweep-selected-count">
            {selected.size} selected
          </span>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={selected.size === 0}
            className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 ${
              selected.size === 0
                ? "bloq-btn-secondary opacity-50 cursor-not-allowed"
                : "bloq-btn-primary bg-[#c43838] hover:bg-[#a82929]"
            }`}
            data-testid="sweep-delete-button"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete selected
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bloq-card p-10 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#82828c]" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[#82828c] text-center py-10">No users match.</p>
      ) : (
        <div className="bloq-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-[#82828c]">
              <tr>
                <th className="px-3 py-3 w-10"></th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Owned WS</th>
                <th className="px-3 py-3">Flag</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const checked = selected.has(u.user_id);
                const disabled = u.is_self || u.is_admin;
                return (
                  <tr
                    key={u.user_id}
                    className={`border-t border-[rgba(26,26,31,0.05)] ${disabled ? "opacity-60" : "hover:bg-[#f5efe6] cursor-pointer"}`}
                    onClick={disabled ? undefined : () => toggle(u.user_id)}
                    data-testid={`sweep-user-row-${u.user_id}`}
                  >
                    <td className="px-3 py-3">
                      {disabled ? (
                        <Square className="w-4 h-4 text-[#cbcbcf]" />
                      ) : checked ? (
                        <CheckSquare className="w-4 h-4 text-[#005e7a]" />
                      ) : (
                        <Square className="w-4 h-4 text-[#82828c]" />
                      )}
                    </td>
                    <td className="px-3 py-3 font-medium">{u.email}</td>
                    <td className="px-3 py-3 text-[#4a4a52]">{u.name}</td>
                    <td className="px-3 py-3 text-[#4a4a52]">{u.owned_workspaces}</td>
                    <td className="px-3 py-3">
                      {u.is_self && (
                        <span className="bloq-pill" style={{ background: "#005e7a", color: "#fff" }}>YOU</span>
                      )}
                      {u.is_admin && !u.is_self && (
                        <span className="bloq-pill" style={{ background: "#b45309", color: "#fff" }}>ADMIN</span>
                      )}
                      {!u.is_admin && isTestUser(u) && (
                        <span className="bloq-pill" style={{ background: "#f5efe6", color: "#82828c" }}>test</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={runSweep}
        busy={busy}
        title="Delete users + their workspaces?"
        body={
          <>
            <p>
              This will cascade-delete <strong>{selected.size}</strong> user
              {selected.size === 1 ? "" : "s"} along with every workspace they own
              (meetings, bookings, contacts, tasks, notes, files, decisions,
              summaries, transcripts, recordings — gone).
            </p>
            <p className="mt-2 text-[#82828c]">
              Your account and any other admins are protected — they will be skipped.
            </p>
          </>
        }
      />
    </div>
  );
}

/* ============================ WORKSPACES TAB ============================ */

function WorkspacesPanel() {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/sweep/inventory`);
      setInventory(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const founderOwned = inventory?.workspaces?.founder_owned_list || [];
  const toggle = (wid) => {
    const w = founderOwned.find((x) => x.workspace_id === wid);
    if (w?.is_protected) return;  // never selectable
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(wid)) n.delete(wid);
      else n.add(wid);
      return n;
    });
  };
  const selectTest = () =>
    setSelected(
      new Set(
        founderOwned
          .filter((w) => !w.is_protected && isTestWs(w))
          .map((w) => w.workspace_id),
      ),
    );
  const clear = () => setSelected(new Set());

  const runSweep = async (token) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/sweep/workspaces`, {
        workspace_ids: Array.from(selected),
        confirm_token: token,
      });
      const summary = r.data.summary || {};
      toast.success(
        `Deleted ${summary.workspaces || selected.size} workspace(s) and their data.`,
        { duration: 5000 },
      );
      setConfirmOpen(false);
      clear();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[#4a4a52]">
        Workspaces you own. The testing agent often creates throwaway workspaces under your
        credentials (BULK_OTHER_WS, SEARCH_ISOLATION_WS, M1055…). Use this tab to nuke them.
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={selectTest}
          className="bloq-btn-secondary text-xs px-3 py-2"
          data-testid="sweep-ws-select-test"
        >
          Select test-named
        </button>
        {selected.size > 0 && (
          <button onClick={clear} className="bloq-btn-secondary text-xs px-3 py-2">
            Clear
          </button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[#82828c]">{selected.size} selected</span>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={selected.size === 0}
            className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 ${
              selected.size === 0
                ? "bloq-btn-secondary opacity-50 cursor-not-allowed"
                : "bloq-btn-primary bg-[#c43838] hover:bg-[#a82929]"
            }`}
            data-testid="sweep-ws-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete selected
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bloq-card p-10 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#82828c]" />
        </div>
      ) : founderOwned.length === 0 ? (
        <p className="text-sm text-[#82828c] text-center py-10">No workspaces.</p>
      ) : (
        <div className="bloq-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-[#82828c]">
              <tr>
                <th className="px-3 py-3 w-10"></th>
                <th className="px-3 py-3">Workspace</th>
                <th className="px-3 py-3">ID</th>
                <th className="px-3 py-3">Flag</th>
              </tr>
            </thead>
            <tbody>
              {founderOwned.map((w) => {
                const checked = selected.has(w.workspace_id);
                const disabled = !!w.is_protected;
                return (
                  <tr
                    key={w.workspace_id}
                    className={`border-t border-[rgba(26,26,31,0.05)] ${disabled ? "opacity-60" : "hover:bg-[#f5efe6] cursor-pointer"}`}
                    onClick={disabled ? undefined : () => toggle(w.workspace_id)}
                    data-testid={`sweep-ws-row-${w.workspace_id}`}
                  >
                    <td className="px-3 py-3">
                      {disabled ? (
                        <Square className="w-4 h-4 text-[#cbcbcf]" />
                      ) : checked ? (
                        <CheckSquare className="w-4 h-4 text-[#005e7a]" />
                      ) : (
                        <Square className="w-4 h-4 text-[#82828c]" />
                      )}
                    </td>
                    <td className="px-3 py-3 font-medium">{w.name}</td>
                    <td className="px-3 py-3 text-xs text-[#82828c] font-mono">{w.workspace_id}</td>
                    <td className="px-3 py-3">
                      {w.is_protected && (
                        <span className="bloq-pill" style={{ background: "#005e7a", color: "#fff" }}>PROTECTED</span>
                      )}
                      {!w.is_protected && isTestWs(w) && (
                        <span className="bloq-pill" style={{ background: "#f5efe6", color: "#82828c" }}>test</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={runSweep}
        busy={busy}
        title="Delete selected workspaces?"
        body={
          <>
            <p>
              This deletes <strong>{selected.size}</strong> workspace
              {selected.size === 1 ? "" : "s"} and every row of data inside them
              (meetings, bookings, tasks, notes, contacts, files, …).
            </p>
            <p className="mt-2 text-[#82828c]">
              You&apos;ll still have at least one workspace remaining — the server refuses
              to leave you with zero.
            </p>
          </>
        }
      />
    </div>
  );
}

/* =========================== OPERATIONAL TAB ============================ */

function OperationalPanel() {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState({
    flush_email_queue: false,
    flush_old_email_logs: false,
    flush_oauth_states: false,
    flush_orphan_meeting_rows: false,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/sweep/inventory`);
      setInventory(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const anySelected = Object.values(flags).some(Boolean);

  const runSweep = async (token) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/sweep/operational`, { ...flags, confirm_token: token });
      const summary = r.data.summary || {};
      const lines = Object.entries(summary).map(([k, v]) => `${k}: ${v}`);
      toast.success(
        lines.length ? `Flushed:\n${lines.join(", ")}` : "Nothing to flush.",
        { duration: 5000 },
      );
      setConfirmOpen(false);
      setFlags({
        flush_email_queue: false,
        flush_old_email_logs: false,
        flush_oauth_states: false,
        flush_orphan_meeting_rows: false,
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const op = inventory?.operational;

  return (
    <div className="flex flex-col gap-4">
      {loading || !op ? (
        <div className="bloq-card p-10 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#82828c]" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          <Toggle
            checked={flags.flush_email_queue}
            onChange={(v) => setFlags({ ...flags, flush_email_queue: v })}
            label="Email queue"
            description={`${op.email_queue_total} pending row(s). Wipes outbound queue (rejected/bounced/test sends).`}
            testid="sweep-op-email-queue"
          />
          <Toggle
            checked={flags.flush_old_email_logs}
            onChange={(v) => setFlags({ ...flags, flush_old_email_logs: v })}
            label="Old email logs (>30 days)"
            description={`${op.email_logs_older_than_30d} historic log row(s) older than 30 days.`}
            testid="sweep-op-email-logs"
          />
          <Toggle
            checked={flags.flush_oauth_states}
            onChange={(v) => setFlags({ ...flags, flush_oauth_states: v })}
            label="OAuth states"
            description={`${op.oauth_states_total} row(s). These are short-lived auth handshakes — safe to wipe.`}
            testid="sweep-op-oauth"
          />
          <Toggle
            checked={flags.flush_orphan_meeting_rows}
            onChange={(v) => setFlags({ ...flags, flush_orphan_meeting_rows: v })}
            label="Orphaned meeting rows"
            description="Participants, chat messages, transcripts, recordings whose parent meeting was already deleted."
            testid="sweep-op-orphans"
          />
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={!anySelected}
          className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 ${
            !anySelected
              ? "bloq-btn-secondary opacity-50 cursor-not-allowed"
              : "bloq-btn-primary bg-[#c43838] hover:bg-[#a82929]"
          }`}
          data-testid="sweep-op-run"
        >
          <Trash2 className="w-3.5 h-3.5" /> Run selected flushes
        </button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={runSweep}
        busy={busy}
        title="Run operational flushes?"
        body={
          <ul className="text-sm list-disc list-inside text-[#4a4a52]">
            {Object.entries(flags)
              .filter(([, v]) => v)
              .map(([k]) => (
                <li key={k}>{k.replace(/_/g, " ")}</li>
              ))}
          </ul>
        }
      />
    </div>
  );
}

function Toggle({ checked, onChange, label, description, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`bloq-card p-4 text-left transition-colors ${checked ? "ring-2 ring-[#005e7a]" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        {checked ? (
          <CheckSquare className="w-5 h-5 text-[#005e7a] flex-shrink-0 mt-0.5" />
        ) : (
          <Square className="w-5 h-5 text-[#82828c] flex-shrink-0 mt-0.5" />
        )}
        <div>
          <p className="font-medium text-sm">{label}</p>
          <p className="text-xs text-[#82828c] mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
}

/* ============================ CONFIRM DIALOG ============================ */

function ConfirmDialog({ open, onClose, onConfirm, busy, title, body }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  const valid = text.trim().toUpperCase() === "DELETE";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sweep-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#c43838]">
            <AlertTriangle className="w-5 h-5" /> {title}
          </DialogTitle>
          <DialogDescription className="text-[#4a4a52]">
            This action cannot be undone. Type <strong>DELETE</strong> below to enable the
            confirmation button.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm text-[#4a4a52]">{body}</div>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type DELETE here"
          className="bloq-input mt-2"
          data-testid="sweep-confirm-input"
          autoFocus
        />
        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary" disabled={busy}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm("DELETE")}
            disabled={!valid || busy}
            className={`bloq-btn-primary ${valid ? "bg-[#c43838] hover:bg-[#a82929]" : "opacity-50 cursor-not-allowed"}`}
            data-testid="sweep-confirm-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>I understand — delete</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============================ SIGNUPS TAB =============================== */
// Lightweight read-only view of recent signups, mirroring the data the
// founder gets in the "New Bloq Beta Signup" admin notification email.
// Driven by GET /admin/sweep/signups (gated by require_bloq_admin).
//
// Filter chips: All / 7d / 30d → since_days query param.
// Substring search filters client-side over the loaded rows.
function SignupsPanel() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ all_time: 0, last_7d: 0, last_30d: 0 });
  const [busy, setBusy] = useState(true);
  const [range, setRange] = useState("all"); // "all" | "7d" | "30d"
  const [q, setQ] = useState("");

  const load = async (selected = range) => {
    setBusy(true);
    try {
      const params = { limit: 200 };
      if (selected === "7d") params.since_days = 7;
      if (selected === "30d") params.since_days = 30;
      const r = await api.get("/admin/sweep/signups", { params });
      setRows(r.data?.signups || []);
      setTotals(r.data?.totals || { all_time: 0, last_7d: 0, last_30d: 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((u) =>
      (u.email || "").toLowerCase().includes(needle) ||
      (u.name || "").toLowerCase().includes(needle)
    );
  }, [q, rows]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="signups-panel">
      {/* Totals strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="All-time signups"  value={totals.all_time}  testid="signups-total-all" />
        <StatCard label="Last 30 days"      value={totals.last_30d} testid="signups-total-30d" />
        <StatCard label="Last 7 days"       value={totals.last_7d}  testid="signups-total-7d" highlight />
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-[#f5efe6] p-1 rounded-md">
          {[["all", "All"], ["30d", "Last 30d"], ["7d", "Last 7d"]].map(([k, lbl]) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              className={`text-xs px-3 py-1.5 ${range === k ? "bg-white shadow-sm" : "text-[#82828c]"}`}
              data-testid={`signups-range-${k}`}
            >{lbl}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#82828c]" />
          <input
            className="bloq-input pl-8"
            placeholder="Filter by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="signups-search"
          />
          {q && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#82828c]"
              onClick={() => setQ("")}
              aria-label="Clear search"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={() => load(range)}
          disabled={busy}
          className="bloq-btn-secondary text-xs px-3 py-1.5 disabled:opacity-50"
          data-testid="signups-refresh"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
        </button>
      </div>

      {/* Table */}
      <div className="bloq-card p-2 sm:p-3 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#82828c]">
                <th className="text-left px-3 py-3">Name</th>
                <th className="text-left px-3 py-3">Email</th>
                <th className="text-left px-3 py-3">Auth</th>
                <th className="text-left px-3 py-3">Verified</th>
                <th className="text-left px-3 py-3">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !busy && (
                <tr><td colSpan={5} className="text-center text-[#82828c] py-8" data-testid="signups-empty">
                  No signups match your filters.
                </td></tr>
              )}
              {filtered.map((u, idx) => (
                <tr
                  key={u.user_id}
                  className={`border-t border-[#f0eadc] ${idx % 2 === 0 ? "bg-[#fbfcfc]" : "bg-white"}`}
                  data-testid={`signups-row-${u.user_id}`}
                >
                  <td className="px-3 py-3 font-medium text-[#1a1a1f]">{u.name || "—"}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[#4a4a52]">
                      <Mail className="w-3 h-3 text-[#82828c]" />
                      {u.email}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
                      style={u.auth_provider === "google"
                        ? { background: "rgba(0,94,122,0.15)", color: "#005e7a" }
                        : { background: "rgba(130,130,140,0.18)", color: "#4a4a52" }}
                    >
                      {u.auth_provider}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {u.email_verified ? (
                      <span className="inline-flex items-center gap-1 text-[#15803d] text-xs font-medium">
                        <ShieldCheck className="w-3.5 h-3.5" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#b45309] text-xs">
                        <ShieldAlert className="w-3.5 h-3.5" /> Pending
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[#4a4a52] text-xs whitespace-nowrap">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-[#82828c]" data-testid="signups-footnote">
        Showing {filtered.length} of {rows.length} loaded signups{range !== "all" ? ` from the last ${range}` : ""}.
        Each new signup also triggers an admin notification to your <code className="px-1 py-0.5 bg-[#f5efe6] rounded text-[10px]">ADMIN_EMAIL</code> inbox.
      </p>
    </div>
  );
}

function StatCard({ label, value, testid, highlight = false }) {
  return (
    <div
      className={`bloq-card p-4 ${highlight ? "border-l-4 border-l-[#005e7a]" : ""}`}
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold">{label}</p>
      <p className="bloq-display text-3xl mt-1 text-[#1a1a1f]">{value}</p>
    </div>
  );
}
